'use client';
import React, { useEffect, useState } from 'react';
import MacroBar from '@/components/ui/MacroBar';
import { forgeFoodService } from '@/lib/services/forgeFoodService';
import { forgeGoalsService } from '@/lib/services/forgeGoalsService';
import { createClient } from '@/lib/supabase/client';

export default function DailyMacros() {
  const [consumed, setConsumed] = useState({ cal: 0, protein: 0, carbs: 0, fat: 0, fibre: 0 });
  const [targets, setTargets] = useState<{ cal: number; protein: number; carbs: number; fat: number } | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase?.auth?.getUser()?.then(async ({ data: { user } }) => {
      if (!user) return;

      const profile = await forgeGoalsService?.getUserProfile(user?.id);
      if (profile?.goals_setup_complete) {
        setTargets({
          cal: profile?.target_calories || 0,
          protein: profile?.target_protein_g || 0,
          carbs: profile?.target_carbs_g || 0,
          fat: profile?.target_fat_g || 0,
        });
      }

      const todayLog = await forgeFoodService?.getTodayLog();
      if (todayLog && todayLog?.length > 0) {
        const totals = todayLog?.reduce(
          (acc, e) => ({
            cal: acc?.cal + (e?.cal || 0),
            protein: acc?.protein + (e?.proteinG || 0),
            carbs: acc?.carbs + (e?.carbsG || 0),
            fat: acc?.fat + (e?.fatG || 0),
            fibre: acc?.fibre + (e?.fibreG || 0),
          }),
          { cal: 0, protein: 0, carbs: 0, fat: 0, fibre: 0 }
        );
        setConsumed(totals);
      }
    });
  }, []);

  if (!targets) {
    return (
      <div className="card-forge rounded-xl p-5">
        <div className="text-forge-label mb-4">Today&apos;s Nutrition</div>
        <div className="text-center py-6">
          <div className="text-2xl mb-2">🎯</div>
          <div className="text-xs font-bold text-foreground mb-1">No Nutrition Targets Set</div>
          <div className="text-2xs text-muted-foreground">Complete goal setup to see your daily macro targets</div>
        </div>
      </div>
    );
  }

  const macros = [
    { label: 'Calories', consumed: Math.round(consumed?.cal), target: targets?.cal, unit: 'kcal', color: 'var(--primary)' },
    { label: 'Protein', consumed: Math.round(consumed?.protein), target: targets?.protein, unit: 'g', color: 'var(--primary)' },
    { label: 'Carbohydrates', consumed: Math.round(consumed?.carbs), target: targets?.carbs, unit: 'g', color: 'var(--accent)' },
    { label: 'Fat', consumed: Math.round(consumed?.fat), target: targets?.fat, unit: 'g', color: 'var(--accent)' },
    { label: 'Fibre', consumed: Math.round(consumed?.fibre), target: 30, unit: 'g', color: 'var(--muted-foreground)' },
  ];

  const calRemaining = targets?.cal - Math.round(consumed?.cal);

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-forge-label">Today&apos;s Nutrition</div>
        <div className="flex items-center gap-2">
          <span className="text-2xs text-muted-foreground">Calories Remaining</span>
          <span className={`font-tabular font-black text-lg ${calRemaining < 0 ? 'text-destructive' : 'text-primary'}`}>
            {calRemaining} kcal
          </span>
        </div>
      </div>
      <div className="space-y-4">
        {macros?.map((m) => (
          <MacroBar
            key={`macro-${m?.label}`}
            label={m?.label}
            consumed={m?.consumed}
            target={m?.target}
            unit={m?.unit}
            color={m?.color}
          />
        ))}
      </div>
      {consumed?.cal === 0 && (
        <div className="mt-4 text-center text-xs text-muted-foreground py-2">
          No food logged today. Head to <span className="text-primary font-bold">Food Logging</span> to start tracking.
        </div>
      )}
    </div>
  );
}